const API_URL = process.env.REACT_APP_CRM_API_URL || 'http://localhost:5000/api/crm';

/**
 * Identidade do caller para regra de privacidade no backend.
 * Setado pelo AuthContext quando o profile carrega; lido a cada request.
 */
let _callerSellerId = null;
export function setCallerSellerId(sellerId) {
  _callerSellerId = sellerId || null;
}

function _buildHeaders(base = {}) {
  const headers = { ...base };
  if (_callerSellerId) {
    headers['X-Caller-Seller-Id'] = _callerSellerId;
  }
  return headers;
}

class ApiService {
  async get(endpoint) {
    const response = await fetch(`${API_URL}${endpoint}`, {
      headers: _buildHeaders(),
    });
    if (!response.ok) throw new Error(`API Error: ${response.status}`);
    return response.json();
  }

  async post(endpoint, data) {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: _buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(`API Error: ${response.status}`);
    return response.json();
  }

  async patch(endpoint, data) {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'PATCH',
      headers: _buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(`API Error: ${response.status}`);
    return response.json();
  }

  async delete(endpoint) {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'DELETE',
      headers: _buildHeaders(),
    });
    if (!response.ok) throw new Error(`API Error: ${response.status}`);
    return response.json();
  }

  async put(endpoint, data) {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'PUT',
      headers: _buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(`API Error: ${response.status}`);
    return response.json();
  }

  // ─── Conversations ───

  /**
   * Lista conversas do vendedor.
   *
   * @param {string} sellerId - ID do vendedor (default: 'default')
   * @param {string} [searchQuery] - Termo de busca por nome/telefone.
   *   Sem query → retorna 50 mais recentes (inbox).
   *   Com query → busca em TODOS os contatos no banco (sem limite de recência).
   */
  async getConversations(sellerId = 'default', searchQuery = '', contactId = '') {
    let url = `/conversations?seller_id=${sellerId}`;
    if (contactId && contactId.trim()) {
      url += `&contact_id=${encodeURIComponent(contactId.trim())}`;
    } else if (searchQuery && searchQuery.trim()) {
      url += `&q=${encodeURIComponent(searchQuery.trim())}`;
    }
    return this.get(url);
  }

  async getMessages(conversationId, limit = 50, before = null) {
    let url = `/messages/conversation/${conversationId}?limit=${limit}`;
    if (before) {
      url += `&before=${encodeURIComponent(before)}`;
    }
    return this.get(url);
  }

  async sendMessage(conversationId, content, mediaUrl = null, mediaType = null, mediaFilename = null, mediaMimetype = null, quotedMessageId = null) {
    const payload = {
      conversation_id: conversationId,
      content: content,
    };
    if (mediaUrl) {
      payload.media_url = mediaUrl;
      payload.media_type = mediaType;
      if (mediaFilename) payload.media_filename = mediaFilename;
      if (mediaMimetype) payload.media_mimetype = mediaMimetype;
    }
    if (quotedMessageId) {
      payload.quoted_message_id = quotedMessageId;
    }
    return this.post('/messages/send', payload);
  }

  async markConversationAsRead(conversationId) {
    return this.post(`/conversations/${conversationId}/read`);
  }

  async markSuggestionUsed(conversationId, used) {
    return this.post(`/conversations/${conversationId}/suggestion-used`, { used });
  }

  /**
   * Arquiva uma conversa.
   * Conversa some da inbox padrão mas histórico permanece.
   */
  async archiveConversation(conversationId) {
    return this.post(`/conversations/${conversationId}/archive`);
  }

  /**
   * Desarquiva uma conversa.
   * Volta a aparecer na inbox padrão.
   */
  async unarchiveConversation(conversationId) {
    return this.post(`/conversations/${conversationId}/unarchive`);
  }

  /**
   * Arquiva múltiplas conversas em uma única chamada (atômico).
   * @param {string[]} ids - Array de conversation IDs
   */
  async archiveBatch(ids) {
    return this.post('/conversations/archive-batch', { ids });
  }

  /**
   * Desarquiva múltiplas conversas em uma única chamada (usado pelo Desfazer em lote).
   * @param {string[]} ids - Array de conversation IDs
   */
  async unarchiveBatch(ids) {
    return this.post('/conversations/unarchive-batch', { ids });
  }

  // ─── Contacts ───
  async updateContact(contactId, data) {
    return this.put(`/contacts/${contactId}`, data);
  }

  async renameContact(contactId, customName) {
    return this.put(`/contacts/${contactId}/rename`, { custom_name: customName });
  }

  async createContactAndConversation(phone, name) {
    // 1. Create contact (or get existing)
    let contact;
    try {
      contact = await this.post('/contacts', { name, phone });
    } catch (err) {
      if (err.message?.includes('409')) {
        // Contact exists, fetch by listing
        const contacts = await this.get('/contacts');
        const normalized = phone.replace(/\D/g, '');
        contact = contacts.find(c => 
          (c.phone || '').replace(/\D/g, '').endsWith(normalized.slice(-8))
        );
        if (!contact) throw new Error('Contato existe mas não encontrado');
      } else {
        throw err;
      }
    }

    // 2. Create conversation
    const conversation = await this.post('/conversations', {
      contact_id: contact.id,
      seller_id: 'default',
    });

    return conversation;
  }

  // ─── Sellers ───
  async getSellers() {
    return this.get('/sellers');
  }

  // ─── Upload ───
  async uploadFile(file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_URL}/media/upload`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) throw new Error(`Upload Error: ${response.status}`);
    return response.json();
  }

  // ─── Tasks ───
  async getTasks(params = {}) {
    const qs = new URLSearchParams();
    qs.set('seller_id', params.sellerId || 'default');
    if (params.status) qs.set('status', params.status);
    if (params.contactId) qs.set('contact_id', params.contactId);
    if (params.search) qs.set('search', params.search);
    if (params.assignedTo) qs.set('assigned_to', params.assignedTo);
    if (params.createdBy) qs.set('created_by', params.createdBy);
    if (params.dueFrom) qs.set('due_from', params.dueFrom);
    if (params.dueTo) qs.set('due_to', params.dueTo);
    if (params.dueSpecial) qs.set('due_special', params.dueSpecial);
    return this.get(`/tasks?${qs.toString()}`);
  }

  async getTaskCounts(sellerId = 'default') {
    return this.get(`/tasks/counts?seller_id=${sellerId}`);
  }

  async createTask(data) {
    return this.post('/tasks', data);
  }

  async updateTask(taskId, data) {
    return this.patch(`/tasks/${taskId}`, data);
  }

  async snoozeTask(taskId) {
    return this.post(`/tasks/${taskId}/snooze`, {});
  }

  async deleteTask(taskId) {
    return this.delete(`/tasks/${taskId}`);
  }

  // ─── Profiles (Ficha de Combate) ───
  async getProfile(contactId) {
    return this.get(`/profiles/contact/${contactId}`);
  }

  async saveProfile(contactId, data) {
    return this.put(`/profiles/contact/${contactId}`, data);
  }

  async addProfileFeedback(contactId, data) {
    return this.post(`/profiles/contact/${contactId}/feedback`, data);
  }

  async dismissDisruption(contactId) {
    return this.post(`/profiles/contact/${contactId}/dismiss-disruption`, {});
  }

  // ─── Busca global (estilo WhatsApp) ───
  async globalSearch(q, sellerId = 'default') {
    if (!q || q.trim().length < 2) return { contacts: [], messages: [] };
    return this.get(`/search?q=${encodeURIComponent(q.trim())}&seller_id=${sellerId}`);
  }
}

export default new ApiService();

